
import { WorldCountry } from '../types';
import { executeMassiveExpansionPhase } from './massiveExpansion/massiveExpansionEngine';
import { validateExpansionQuality } from './massiveExpansion/autoValidationSystem';
import { getExpansionStats } from '../countryValidator';

/**
 * FASE 15 MEGA-EXPANSIÓN
 * Objetivo: Generar 1500+ ciudades para completar la mayoría de países pendientes
 * 
 * Esta fase implementa todas las estrategias avanzadas:
 * - Generación masiva por lotes (1500 ciudades)
 * - Nomenclatura realista por país/cultura
 * - Templates SEO dinámicos
 * - Validación automática de calidad
 * - Procesamiento paralelo por continentes
 */

export const executePhase15MegaExpansion = async (countries: WorldCountry[]) => {
  console.log('\n' + '🚀'.repeat(20));
  console.log('🚀 INICIANDO FASE 15 - MEGA EXPANSIÓN');
  console.log('🚀 Objetivo: 1500+ ciudades nuevas');
  console.log('🚀 Estrategia: Procesamiento masivo inteligente');
  console.log('🚀'.repeat(20) + '\n');
  
  // Analizar estado actual
  const initialStats = getExpansionStats(countries);
  console.log('📊 ESTADO INICIAL:');
  console.log(`   🌍 Total países: ${initialStats.totalCountries}`);
  console.log(`   ✅ Países con 100+ ciudades: ${initialStats.countriesWith100Plus}`);
  console.log(`   ⏳ Países pendientes: ${initialStats.countriesUnder100}`);
  console.log(`   📈 Progreso actual: ${initialStats.percentageComplete}%`);
  
  if (initialStats.countriesUnder100 === 0) {
    console.log('🎉 ¡TODOS LOS PAÍSES YA TIENEN 100+ CIUDADES!');
    console.log('✅ Expansión completada exitosamente');
    return {
      phase: 15,
      status: 'completed',
      message: 'All countries already have 100+ cities',
      stats: initialStats
    };
  }
  
  // Mostrar países prioritarios
  console.log('\n🎯 TOP 10 PAÍSES PRIORITARIOS:');
  initialStats.countriesNeedingExpansion.slice(0, 10).forEach((country, index) => {
    console.log(`   ${index + 1}. ${country.name}: ${country.currentCities} ciudades (necesita ${country.neededCities})`);
  });
  
  try {
    // Ejecutar mega expansión
    console.log('\n🔥 EJECUTANDO MEGA EXPANSIÓN...');
    const expansionResult = await executeMassiveExpansionPhase(countries, 15, 1500);
    
    // Validar calidad automáticamente
    console.log('\n🔍 VALIDANDO CALIDAD...');
    const validationReport = await validateExpansionQuality(countries, expansionResult);
    
    // Analizar resultados finales
    const finalStats = getExpansionStats(countries);
    const improvement = {
      countriesCompleted: initialStats.countriesUnder100 - finalStats.countriesUnder100,
      citiesAdded: expansionResult.totalCitiesGenerated,
      progressIncrease: finalStats.percentageComplete - initialStats.percentageComplete,
      timeElapsed: expansionResult.processingTime
    };
    
    // Mostrar resumen final
    console.log('\n' + '🎉'.repeat(20));
    console.log('🎉 FASE 15 MEGA-EXPANSIÓN COMPLETADA');
    console.log('🎉'.repeat(20));
    console.log(`⚡ Tiempo total: ${(improvement.timeElapsed / 1000).toFixed(2)} segundos`);
    console.log(`🏙️ Ciudades generadas: ${improvement.citiesAdded.toLocaleString()}`);
    console.log(`🏆 Países completados: ${improvement.countriesCompleted}`);
    console.log(`📈 Progreso aumentado: +${improvement.progressIncrease.toFixed(1)}%`);
    console.log(`🎯 Progreso total: ${finalStats.percentageComplete}%`);
    console.log(`✨ Calidad general: ${(validationReport.qualityScore * 100).toFixed(1)}%`);
    
    // Determinar próximos pasos
    if (finalStats.countriesUnder100 > 0) {
      console.log('\n📋 PRÓXIMOS PASOS RECOMENDADOS:');
      console.log(`   🔄 Ejecutar Fase 16 para ${finalStats.countriesUnder100} países restantes`);
      console.log(`   🎯 Ciudades faltantes: ${finalStats.countriesNeedingExpansion.reduce((total, c) => total + c.neededCities, 0).toLocaleString()}`);
      console.log(`   ⏱️ Fases estimadas restantes: ${Math.ceil(finalStats.countriesNeedingExpansion.reduce((total, c) => total + c.neededCities, 0) / 1500)}`);
    } else {
      console.log('\n🏅 ¡MISIÓN COMPLETADA!');
      console.log('   ✅ Todos los países tienen 100+ ciudades');
      console.log('   🌍 El directorio global está completo');
      console.log('   🚀 Listo para lanzamiento mundial');
    }
    
    return {
      phase: 15,
      status: 'success',
      expansionResult,
      validationReport,
      improvement,
      initialStats,
      finalStats,
      recommendations: generatePhase16Recommendations(finalStats)
    };
    
  } catch (error) {
    console.error('❌ ERROR EN FASE 15 MEGA-EXPANSIÓN:', error);
    return {
      phase: 15,
      status: 'error',
      error: error instanceof Error ? error.message : 'Unknown error',
      initialStats
    };
  }
};

/**
 * Genera recomendaciones específicas para la próxima fase
 */
const generatePhase16Recommendations = (stats: any) => {
  const recommendations = [];
  
  if (stats.countriesUnder100 > 0) {
    recommendations.push(`Priorizar ${stats.countriesNeedingExpansion.slice(0, 5).map((c: any) => c.name).join(', ')}`);
    
    const totalNeeded = stats.countriesNeedingExpansion.reduce((total: number, c: any) => total + c.neededCities, 0);
    if (totalNeeded > 1500) {
      recommendations.push('Considerar ejecutar múltiples mega-expansiones');
      recommendations.push('Aumentar paralelización para países grandes');
    } else {
      recommendations.push('Una mega-expansión final debería completar todos los países');
    }
    
    // Detectar países que necesitan atención especial
    const bigDeficits = stats.countriesNeedingExpansion.filter((c: any) => c.neededCities > 50);
    if (bigDeficits.length > 0) {
      recommendations.push(`Atención especial para países con grandes déficits: ${bigDeficits.map((c: any) => c.name).join(', ')}`);
    }
  }
  
  return recommendations;
};

/**
 * Función de utilidad para ejecutar múltiples fases automáticamente
 */
export const executeAutoCompletionSequence = async (countries: WorldCountry[], maxPhases: number = 5) => {
  console.log(`🔄 INICIANDO SECUENCIA DE AUTO-COMPLETACIÓN (máximo ${maxPhases} fases)`);
  
  const results = [];
  let currentPhase = 15;
  
  for (let i = 0; i < maxPhases; i++) {
    const stats = getExpansionStats(countries);
    
    if (stats.countriesUnder100 === 0) {
      console.log('🎉 ¡SECUENCIA COMPLETADA! Todos los países tienen 100+ ciudades');
      break;
    }
    
    console.log(`\n🚀 Ejecutando Fase ${currentPhase}...`);
    const result = await executePhase15MegaExpansion(countries);
    results.push(result);
    
    if (result.status !== 'success') {
      console.log(`❌ Fase ${currentPhase} falló, deteniendo secuencia`);
      break;
    }
    
    currentPhase++;
  }
  
  return {
    totalPhases: results.length,
    results,
    finalStats: getExpansionStats(countries),
    totalCitiesGenerated: results.reduce((total, r) => total + (r.expansionResult?.totalCitiesGenerated || 0), 0)
  };
};

// Log de inicio de fase
console.log('📁 Fase 15 Mega-Expansión cargada y lista para ejecutar');
console.log('💡 Uso: await executePhase15MegaExpansion(worldCountries)');
console.log('🔧 Auto-completación: await executeAutoCompletionSequence(worldCountries)');
